import { getWeekOfMonth } from 'date-fns';
import BaseObject from './BaseObject';
import Occasion from '../helpers/Occasion';
import { capitalize } from '../helpers/string';
import { ordinalize } from '../helpers/number';

export type CadenceType = 'weekly' | 'monthly' | 'quarterly' | 'yearly' | 'auto' | 'none';
export type Qualifier = 'first' | 'second' | 'third' | 'fourth' | 'last';

const typeToTitle = (cadenceType: CadenceType): string => {
  switch (cadenceType) {
    case 'auto':
      return 'Automatic';

    case 'none':
      return 'Disable';

    default:
      return capitalize(cadenceType);
  }
};

const weekToQualifier = {
  1: 'first',
  2: 'second',
  3: 'third',
  4: 'fourth',
  5: 'last',
};

const shortQualifier = {
  first: '1st',
  second: '2nd',
  third: '3rd',
  fourth: '4th',
  last: 'last',
};

export const qualifierIntToQualifier = {
  ...weekToQualifier,
  '-1': 'last',
};

const cadenceIntToType = {
  1: 'weekly',
  2: 'monthly',
  3: 'quarterly',
  4: 'yearly',
};

const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const dayToIdx = daysOfWeek.reduce(
  (dayToIdxMap, day, idx) => ({ ...dayToIdxMap, [day.toLowerCase()]: idx }),
  {},
);

export const dateDayToCadenceDayOfWeek = (day: number): number => (day == 0 ? 6 : day - 1);

const cadenceTypeFromProps = (props: any): CadenceType => {
  let cadenceType = cadenceIntToType[props.cadence] || props.type;

  if (props.is_disabled || (!props.is_automatic && !props.type)) {
    cadenceType = 'none';
  } else if (props.is_automatic) {
    cadenceType = 'auto';
  }

  return cadenceType;
};

const dayOfWeekFromProps = (props: any): number | undefined => {
  let dayOfWeek = props.day_of_week || props.dayOfWeek;

  if (typeof dayOfWeek == 'string') {
    dayOfWeek = dayToIdx[dayOfWeek];
  }

  return dayOfWeek !== undefined ? dayOfWeek : null;
};

const qualifierFromProps = (props: any): Qualifier | undefined => {
  let qualifier = props.qualifier;

  if (typeof qualifier == 'number') {
    qualifier = qualifierIntToQualifier[qualifier];
  }

  return qualifier;
};

const fieldsFromProps = (props: any): Partial<Cadence> & { cadenceType: CadenceType } => {
  return {
    cadenceType: cadenceTypeFromProps(props),
    dayOfWeek: dayOfWeekFromProps(props),
    dayOfMonth: props.day_of_month || props.dayOfMonth,
    qualifier: qualifierFromProps(props),
  };
};

export default class Cadence extends BaseObject {
  type: CadenceType;
  dayOfWeek: number; // zero-indexed, monday == 0
  dayOfMonth: number;
  month: number; // january == 1
  qualifier: Qualifier;

  constructor(props) {
    super(props);

    let { cadenceType, dayOfWeek, dayOfMonth, qualifier } = fieldsFromProps(props);

    // For the purposes of our preview, we default to the current date for dayOfWeek, dayOfMonth, and month.
    const now = new Date();
    const hasDayOfMonth = typeof dayOfMonth == 'number';

    this.type = cadenceType;
    this.dayOfWeek = dayOfWeek; //!== undefined ? dayOfWeek : null //dateDayToCadenceDayOfWeek(now.getDay())
    this.dayOfMonth = hasDayOfMonth ? dayOfMonth : now.getDate();
    this.month = props.month !== undefined ? props.month : now.getMonth() + 1;

    // If dayOfMonth is not set, we guess at the qualifier
    let weekOfMonth = getWeekOfMonth(this.refDate);
    if (!hasDayOfMonth) {
      weekOfMonth = getWeekOfMonth(now);
    }

    this.qualifier = qualifier || weekToQualifier[weekOfMonth];

    if (cadenceType == 'weekly' && typeof dayOfWeek != 'number') {
      this.dayOfWeek = 0;
    }
  }

  get refDate(): Date {
    return new Date(new Date().getFullYear(), this.month, this.dayOfMonth);
  }

  get interval(): string {
    if (this.type && this.type !== 'auto') {
      return this.type.slice(0, -2);
    } else {
      return 'auto';
    }
  }

  get formattedDay(): string {
    if (this.type == 'weekly') {
      return daysOfWeek[this.dayOfWeek] || daysOfWeek[this.dayOfMonth - 1] || daysOfWeek[0];
    } else if (this.type == 'yearly') {
      const occasion = new Occasion(new Date(2000, this.month - 1, this.dayOfMonth));
      return occasion.format('MMMM do');
    } else if (this.type == 'auto') {
      return 'auto';
    } else if (typeof this.dayOfWeek != 'number') {
      return `${ordinalize(this.dayOfMonth)} day`;
    }

    return `${shortQualifier[this.qualifier]} ${daysOfWeek[this.dayOfWeek]}`;
  }

  // For now we are limiting reminders to 8:00 AM, but we may make this
  // editable/configurable in the future
  get formattedTime(): string {
    return '8:00 AM';
  }

  // For doing Cadence instance comparisons
  get string(): string {
    return this.interval + this.formattedDay + this.formattedTime;
  }

  get title(): string {
    return typeToTitle(this.type);
  }

  get isUserConfigured(): boolean {
    return ['weekly', 'monthly', 'quarterly', 'yearly'].includes(this.type);
  }

  get toJSON() {
    let day_of_month = this.dayOfMonth;

    if (['monthly', 'quarterly'].includes(this.type) && typeof this.dayOfWeek == 'number') {
      day_of_month = null;
    }

    return {
      cadence: this.isUserConfigured ? this.type : 'weekly',
      qualifier: this.qualifier,
      day_of_week: this.isUserConfigured ? daysOfWeek[this.dayOfWeek]?.toLowerCase() : null,
      day_of_month,
      month: this.type == 'yearly' ? this.month : null,
      is_automatic: this.type == 'auto',
    };
  }
}
